import { styled } from '@mui/material/styles'
import { differenceInMinutes } from 'date-fns'
import { useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import {
  ANY_RESOURCE_ID,
  ANY_INTERNAL_RESOURCE_ID,
  ANY_EXTERNAL_RESOURCE_ID,
  AvailabilityDefaultParameters,
  ToastSubType,
} from '../../../../../../../constants'
import { useDateFormat, useIsFitterSchedulingMode, useNotification } from '../../../../../../../hooks'
import { theme } from '../../../../../../../modules/App/Providers/Theme/theme'
import { AppointmentFormData, Resource, ResourceAvailability } from '../../../../../../../types'
import { Spacer } from '../../../../../../Spacer'
import { useAvailability } from '../../../Providers/Availability'
import { useAvailabilityParameters } from '../../../useAvailabilityParameters'
import { TimeBlock as TimeBlockComponent } from './TimeBlock'
import { TimeSlotsSkeleton } from './TimeSlotsSkeleton'
import { Title } from './Title'
import { mergeAvailabilities } from '../../../../../../../util'
import { useTranslation } from 'react-i18next'
import { filterByResource } from 'util/filterByResource'

const TIME_SLOT_DATA_ATTRIBUTE = 'data-time-slots'
const TIME_SLOT_ANCHOR_SELECTOR = `[${TIME_SLOT_DATA_ATTRIBUTE}="true"]`

const Container = styled('div')<{
  isFitterSchedulingMode?: boolean
}>`
  align-content: start;
  color: ${theme.palette.grey[500]};
  display: grid;
  font-size: 0.75rem;
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  row-gap: 1rem;
  text-align: center;

  @media (min-width: ${theme.breakpoints.values.md}px) {
    height: 17rem;
    overflow-y: auto;
  }
`

const filterAvailabilitiesByResourceId = (availabilities: ResourceAvailability[], resourceId: string, resources?: Resource[]) => {
  if (!availabilities) return []

  if (resourceId === ANY_RESOURCE_ID) {
    // Deduplicate by time - when "Anyone" is selected, show only one slot per time
    const seenTimes = new Set<string>()
    return availabilities.filter((availability) => {
      const timeKey = availability.date_start.toISOString()
      if (seenTimes.has(timeKey)) return false
      seenTimes.add(timeKey)
      return true
    })
  }

  if (resourceId === ANY_INTERNAL_RESOURCE_ID || resourceId === ANY_EXTERNAL_RESOURCE_ID) {
    const matchingResourceIds = new Set(resources?.filter(filterByResource(resourceId)).map((r) => r.id))
    const seenTimes = new Set<string>()
    return availabilities.filter((availability) => {
      const hasMatchingResource = availability.resource_ids.some((id) => matchingResourceIds.has(id))
      if (!hasMatchingResource) return false
      const timeKey = availability.date_start.toISOString()
      if (seenTimes.has(timeKey)) return false
      seenTimes.add(timeKey)
      return true
    })
  }

  return availabilities.filter(({ resource_ids }) => resource_ids.includes(resourceId))
}

const Availabilities = () => {
  const { t } = useTranslation()

  const [clientStartDate, customer_timezone, specialist_id] = useWatch<AppointmentFormData>({
    name: ['clientStartDate', 'customer_timezone', 'specialist_id'],
  })

  const { notify } = useNotification()
  const params = useAvailabilityParameters()
  const resources = params?.resources || []
  const { data: rawData, isError, isLoading } = useAvailability()

  const data = mergeAvailabilities(rawData)

  const isFitterSchedulingMode = useIsFitterSchedulingMode()
  const resourceHasIntegration = Boolean(rawData?.resourceHasCalendarIntegration?.[specialist_id as string])

  const { dateToISODateKey, dateTo24hrDisplayTime } = useDateFormat()
  const dayKey = dateToISODateKey(clientStartDate as Date)
  const availabilities = filterAvailabilitiesByResourceId(data?.[dayKey], specialist_id as string, resources)
  const hasAvailabilitiesForSelectedDay = Boolean(availabilities && availabilities.length)
  const hasAnyAvailabilities = Object.keys(data).some(
    (dayKey) => filterAvailabilitiesByResourceId(data?.[dayKey], specialist_id as string, resources)?.length
  )

  useEffect(() => {
    if (resourceHasIntegration && !hasAnyAvailabilities) {
      notify(
        {
          message: `No Availabilities found in the next ${AvailabilityDefaultParameters.MAX_AVAILABILITIES_DATE_RANGE} days.`,
          type: ToastSubType.NO_AVAILABILITIES_FOUND_WITHIN_RANGE,
        },
        'warning'
      )
    }
  }, [hasAnyAvailabilities])

  const hasErroredOrIsMissingIds = isError || !(clientStartDate && customer_timezone && specialist_id)

  if (hasErroredOrIsMissingIds) {
    return <Container isFitterSchedulingMode={isFitterSchedulingMode} />
  }

  if (isLoading) {
    return (
      <>
        <Title />
        <Spacer count={0.5} />
        <TimeSlotsSkeleton />
      </>
    )
  }

  if (!hasAvailabilitiesForSelectedDay) {
    return <Container isFitterSchedulingMode={isFitterSchedulingMode}>{t('common.label.noAvailabilitiesForThisDay')}</Container>
  }

  const [firstAvailability] = availabilities
  const duration = differenceInMinutes(firstAvailability.date_end, firstAvailability.date_start)

  const showDurations = availabilities.some((availability) => {
    const thisDuration = differenceInMinutes(availability.date_end, availability.date_start)

    return duration !== thisDuration
  })

  return (
    <>
      {hasAvailabilitiesForSelectedDay && <Title />}
      <Spacer count={0.5} />
      <div {...{ [TIME_SLOT_DATA_ATTRIBUTE]: true }} style={{ position: 'relative', height: 1, top: '4rem', zIndex: 0 }} />
      <Container isFitterSchedulingMode={isFitterSchedulingMode} as="ul">
        {availabilities?.map((block: ResourceAvailability) => {
          const duration = differenceInMinutes(block.date_end, block.date_start)
          const key = `${dateTo24hrDisplayTime(block.date_start)}${duration}`

          return <TimeBlockComponent block={block} key={key} showDuration={showDurations} />
        })}
      </Container>
    </>
  )
}

export { Availabilities, TIME_SLOT_ANCHOR_SELECTOR }
